import { getCurrentReceivedUrl } from "@/common/currentPageUrl";
import { getHostnameFromUrl, getPublicSubdomainPart } from "@/common/domain";
import { postRequest } from "@/v2/libs/axios";
import { AgentSchema, PaidChatSettings } from "@/v2/types/client";
import {
  RagResponse,
  PostFormDataRequest,
  PostFormDataResponse,
  AgentRequest,
  StartWebChatRequest,
  PostChatMessageRequest,
  GetRAGInfoRequest,
  ChatResponse,
  StartWebChatResponse,
  CreateSessionChatRequest,
  CreateSessionChatResponse,
} from "@/v2/types/schema";
import { webConfig } from "@/v2/utils/config";
import { AUTH_KEY } from "@/v2/utils/constants";
import {
  DEV_STUDIO_MOCK_AGENT_ID,
  shouldUseLocalAgentPreview,
  shouldUseLocalFeaturePreview,
} from "@/v2/libs/publicFeaturePolicy";
import {
  applyLocalPreviewAgent,
} from "@/v2/libs/localPreview";
import {
  loadLocalPreviewAgentSettings,
  loadRemoteLocalPreviewAgentSettings,
} from "@/v2/libs/localPreviewSettings";

const { VITE_BACKEND_API_URL, VITE_RAG_URL } = webConfig;

const applyLocalConsentPreview = (
  agent: AgentSchema,
  agentId?: string,
): AgentSchema => {
  if (!agentId || !shouldUseLocalAgentPreview(agentId)) return agent;

  const localSettings = loadLocalPreviewAgentSettings(agentId);
  if (!localSettings.start_consent_required) return agent;

  return {
    ...agent,
    start_consent_required: true,
    start_consent_label:
      localSettings.start_consent_label || agent.start_consent_label,
    start_terms_text: localSettings.start_terms_text || agent.start_terms_text,
    start_privacy_text:
      localSettings.start_privacy_text || agent.start_privacy_text,
  };
};

type PublicSpecialFeatureConfigResponse = {
  featureConfigs?: Record<string, Record<string, unknown>>;
};

const getStringSetting = (
  settings: Record<string, unknown>,
  key: string,
) => {
  const value = settings[key];
  return typeof value === "string" ? value : undefined;
};

const getNumberSetting = (
  settings: Record<string, unknown>,
  key: string,
) => {
  const value = settings[key];
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const loadPublicRuntimeSettings = async (
  agentId?: string,
): Promise<Partial<AgentSchema>> => {
  if (!agentId) return {};

  try {
    const response = await postRequest<
      PublicSpecialFeatureConfigResponse,
      Record<string, never>
    >(`/web-special-feature-configs/${agentId}`, {});
    const settings = response.featureConfigs?.start_consent;
    const paidChat = response.featureConfigs?.paid_chat;
    const nextSettings: Partial<AgentSchema> = {};

    if (settings && settings.enabled === true) {
      nextSettings.start_consent_required = Boolean(settings.required);
      nextSettings.start_consent_label = getStringSetting(settings, "label");
      nextSettings.start_terms_text = getStringSetting(settings, "termsText");
      nextSettings.start_privacy_text = getStringSetting(settings, "privacyText");
    }

    if (paidChat && paidChat.enabled === true) {
      const paidChatSettings: PaidChatSettings = {
        enabled: true,
        status:
          paidChat.status === "private" ? "private" : "published",
        paymentType:
          paidChat.paymentType === "ticket" ||
          paidChat.paymentType === "one_time" ||
          paidChat.paymentType === "time"
            ? paidChat.paymentType
            : "monthly",
        price: getNumberSetting(paidChat, "price"),
        freeMessageLimit: getNumberSetting(paidChat, "freeMessageLimit"),
        accessPeriodDays: getNumberSetting(paidChat, "accessPeriodDays"),
        title: getStringSetting(paidChat, "title"),
        description: getStringSetting(paidChat, "description"),
        buttonLabel: getStringSetting(paidChat, "buttonLabel"),
        checkoutMode: "auto",
        checkoutUrl: getStringSetting(paidChat, "checkoutUrl"),
        provider: "stripe",
      };
      nextSettings.paid_chat_settings = paidChatSettings;
    }

    return nextSettings;
  } catch {
    return {};
  }
};

type LocalDashboardEventType =
  | "page_view"
  | "conversation"
  | "material_request"
  | "calendar_reservation"
  | "calendar_reservation_click"
  | "presentation_completion"
  | "faq_self_resolved"
  | "catalog_view"
  | "catalog_detail"
  | "catalog_cta"
  | "gacha_view"
  | "gacha_draw"
  | "gacha_cta"
  | "diagnosis_start"
  | "diagnosis_complete"
  | "diagnosis_cta"
  | "quiz_start"
  | "quiz_complete"
  | "quiz_cta"
  | "health_check_view"
  | "health_check_start"
  | "health_check_result"
  | "health_check_cta"
  | "exit_popup_view"
  | "exit_popup_cta"
  | "affiliate_link_view"
  | "affiliate_link_click";

export type HealthCheckClientConfig = {
  enabled: boolean;
  sdkConfigName?: string;
  sdkVersion?: string;
  sdkType?: string;
  webApiKey?: string;
  monthlyScanLimit?: number;
  message?: string | null;
};

// Validate required environment variables
if (!VITE_RAG_URL || !VITE_BACKEND_API_URL) {
  throw new Error(
    "VITE_RAG_URL or VITE_BACKEND_API_URL is not defined in the environment variables.",
  );
}

export const getAgent = async (request: AgentRequest) => {
  const response = await postRequest<AgentSchema, AgentRequest>(
    "/web-agent-data",
    request,
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  const shouldApplyPreview = shouldUseLocalAgentPreview(request.agent_id);
  const remotePreviewSettings = shouldApplyPreview
    ? await loadRemoteLocalPreviewAgentSettings(request.agent_id)
    : {};
  const agent = shouldApplyPreview
    ? applyLocalPreviewAgent(response, request.agent_id, remotePreviewSettings)
    : response;
  const publicRuntimeSettings = await loadPublicRuntimeSettings(
    request.agent_id,
  );
  const agentWithConsent = applyLocalConsentPreview({
    ...agent,
    ...publicRuntimeSettings,
  }, request.agent_id);

  void postLocalDashboardEvent({
    agent_id: request.agent_id,
    chat_settings_id: agentWithConsent.chat_settings_id,
    event_type: "page_view",
  });

  return agentWithConsent;
};

export const postLocalDashboardEvent = async (request: {
  agent_id?: string;
  chat_settings_id?: string;
  event_type: LocalDashboardEventType;
  metadata?: Record<string, unknown>;
}) => {
  try {
    await fetch(`${VITE_BACKEND_API_URL}/web-local-dashboard-event`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...request,
        occurred_at: new Date().toISOString(),
      }),
      keepalive: true,
    });
  } catch {
    // ローカル開発用の軽量計測なので、ユーザー体験を止めない。
  }
};

export const getHealthCheckClientConfig = async (agentId: string) => {
  const useLocal = shouldUseLocalFeaturePreview(agentId);
  const targetAgentId = useLocal ? DEV_STUDIO_MOCK_AGENT_ID : agentId;
  const endpoint = useLocal
    ? `/web-local-health-check-client-config/${targetAgentId}`
    : `/web-health-check-client-config/${targetAgentId}`;

  return postRequest<HealthCheckClientConfig, Record<string, never>>(
    endpoint,
    {},
  );
};

export const createSessionChat = async (request: CreateSessionChatRequest) => {
  const response = await postRequest<
    CreateSessionChatResponse,
    CreateSessionChatRequest
  >(`/web-create-session-chat/${request.agent_id}`, request);

  return response;
};

export const startWebChat = async (
  request: StartWebChatRequest & { agent_id: string },
) => {
  const response = await postRequest<StartWebChatResponse, StartWebChatRequest>(
    `/start-web-chat/${request.agent_id}`,
    request,
  );

  return response;
};

export async function postChatMessage(
  request: PostChatMessageRequest & { agent_id: string },
): Promise<ChatResponse> {
  const { agent_id, ...data } = request;
  const response = await postRequest<ChatResponse, PostChatMessageRequest>(
    `/web-agent-chat/${agent_id}`,
    data,
  );

  return response;
}

export async function postChatStreamingMessage(
  request: PostChatMessageRequest & { agent_id: string; chat_id: string },
) {
  const { agent_id, ...data } = request;
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  const authData = JSON.parse(localStorage.getItem(AUTH_KEY) || "{}");
  // Authorization
  if (authData) {
    headers.Authorization = `Bearer ${authData.access_token}`;
  }

  const receivedUrl = getCurrentReceivedUrl() || document.referrer || "";
  if (receivedUrl) {
    const hostname = getHostnameFromUrl(receivedUrl);
    const publicPage = hostname ? getPublicSubdomainPart(hostname) : null;
    if (publicPage) {
      headers["public-page"] = publicPage;
    }
  }
  const response = await fetch(
    `${VITE_BACKEND_API_URL}/web-agent-chat-streaming/${agent_id}`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    },
  );

  return response;
}

/**
 * Get RAG information from external RAG service
 * Note: Uses absolute URL because RAG service may be on a different domain
 * than the main backend API
 *
 * @param request - RAG info request with agent_id
 * @returns RAG response data
 * @throws Error if VITE_RAG_URL is not defined
 */
export async function getRAGInfo(
  request: GetRAGInfoRequest & { agent_id: string },
): Promise<RagResponse> {
  if (!VITE_RAG_URL) {
    throw new Error(
      "RAG URL is not configured. Please check VITE_RAG_URL environment variable.",
    );
  }

  const { agent_id, ...data } = request;
  const response = await postRequest<RagResponse, GetRAGInfoRequest>(
    `${VITE_RAG_URL}/rag-info/${agent_id}`,
    data,
  );

  return response;
}

export async function postFormData(
  formData: PostFormDataRequest,
): Promise<PostFormDataResponse> {
  const response = await postRequest<PostFormDataResponse, PostFormDataRequest>(
    "/save-forms",
    {
      ...formData,
    },
  );

  return response;
}
